import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { sign_in_with_google, type AdminProfile } from '../auth';
import { ApiError } from '../api';

const REASON_MESSAGES: Record<string, string> = {
  email_not_verified: 'Your Google account email is not verified.',
  wrong_workspace:
    'You must sign in with a captomi.com Google Workspace account.',
  wrong_domain: 'Your email must be a captomi.com address.',
  not_admin:
    'You are not on the admin allowlist. Contact an existing admin to get access.',
  deactivated: 'Your admin access has been deactivated.',
  invalid_token: 'Sign-in token could not be verified. Please try again.',
};

export default function Login({
  on_signed_in,
}: {
  on_signed_in: (admin: AdminProfile) => void;
}) {
  const [error, set_error] = useState<string | null>(null);
  const [busy, set_busy] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();

  async function handle_sign_in() {
    set_busy(true);
    set_error(null);
    try {
      const admin = await sign_in_with_google();
      on_signed_in(admin);
      navigate(params.get('next') ?? '/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        set_error(REASON_MESSAGES[err.reason] ?? `Sign-in failed: ${err.reason}`);
      } else if ((err as { code?: string })?.code === 'auth/popup-closed-by-user') {
        // user closed the popup; not an error worth surfacing
      } else {
        set_error('Sign-in failed. Please try again.');
      }
    } finally {
      set_busy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background:
          'radial-gradient(ellipse at top, var(--green-50) 0%, var(--bg) 70%)',
      }}
    >
      <div className="card" style={{ width: 460 }}>
        <img
          src="/img/captomi-logo-2.png"
          alt="Captomi"
          style={{ height: 88, width: 'auto', display: 'block', margin: '0 auto 28px' }}
          onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')}
        />
        <span className="eyebrow">Internal</span>
        <h1 style={{ marginTop: 20, marginBottom: 8 }}>Captomi Admin</h1>
        <p className="muted" style={{ marginBottom: 28 }}>
          Sign in with your captomi.com Google Workspace account.
        </p>
        {error && (
          <div className="error" style={{ marginBottom: 20 }}>
            {error}
          </div>
        )}
        <button onClick={handle_sign_in} disabled={busy} style={{ width: '100%' }}>
          {busy ? 'Signing in...' : 'Sign in with Google'}
        </button>
      </div>
    </div>
  );
}
